'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@/components/modals/Modal';
import { Accordion } from '@/components/ui-components/Accordion';

export interface RecordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  records: any[];
  title?: string;
}

const RecordsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RecordsCount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
`;

const StyledAccordion = styled(Accordion)`
  background: #1e1e1e;
  border: 2px solid #3a3a3a;
  
  /* Override Accordion header styles for dark theme */
  > div:first-child {
    background: #1e1e1e;
    
    &:hover {
      background: #2a2a2a;
    }
  }
  
  /* Style the title */
  > div:first-child > div:first-child {
    font-size: 14px;
    font-weight: 500;
    color: #e5e5e5;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  }
  
  /* Style the collapse icon */
  > div:first-child > div:last-child {
    background: #2a2a2a;
    border-color: #4a4a4a;
    
    &:hover {
      background: #3a3a3a;
      border-color: #5a5a5a;
    }
    
    svg {
      color: #9ca3af;
    }
  }
`;

const RecordContent = styled.div`
  padding: 16px;
  background: #1e1e1e;
  border-top: 1px solid #3a3a3a;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #e5e5e5;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 3px;
  }
`;

const JSONContainer = styled.div`
  white-space: pre;
`;

const JSONKey = styled.span`
  color: #e5e5e5;
`;

const JSONString = styled.span`
  color: #FB6F2A;
`;

const JSONNumber = styled.span`
  color: #FB6F2A;
`;

const JSONBoolean = styled.span`
  color: #FB6F2A;
`;

const JSONNull = styled.span`
  color: #64748b;
`;

const JSONBracket = styled.span`
  color: #9ca3af;
`;

const CollapsibleObject = styled.div`
  margin-left: 16px;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  margin-right: 4px;
  font-size: 12px;
  
  &:hover {
    color: #e5e5e5;
  }
`;

/**
 * Renders JSON with syntax highlighting and collapsible objects
 */
const JSONRenderer: React.FC<{ data: any; depth?: number }> = ({ data, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});
  
  const toggleCollapse = (key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const renderValue = (value: any, key?: string, indent: number = 0): React.ReactElement => {
    const indentStr = '  '.repeat(indent);
    const keyPath = key ? `${depth}-${key}` : `${depth}-root`;
    
    if (value === null) {
      return <JSONNull>null</JSONNull>;
    }
    
    if (typeof value === 'string') {
      return <JSONString>"{value}"</JSONString>;
    }
    
    if (typeof value === 'number') {
      return <JSONNumber>{value}</JSONNumber>;
    }
    
    if (typeof value === 'boolean') {
      return <JSONBoolean>{value.toString()}</JSONBoolean>;
    }
    
    if (Array.isArray(value)) {
      if (value?.length === 0) {
        return <JSONBracket>[]</JSONBracket>;
      }
      
      return (
        <>
          <ToggleButton onClick={() => toggleCollapse(keyPath)}>
            {collapsed[keyPath] ? '▸' : '▾'}
          </ToggleButton>
          <JSONBracket>[</JSONBracket>
          {!collapsed[keyPath] && (
            <CollapsibleObject>
              {value.map((item, index) => (
                <div key={index}>
                  {indentStr}  {renderValue(item, `${keyPath}-${index}`, indent + 1)}
                  {index < value?.length - 1 && ','}
                </div>
              ))}
            </CollapsibleObject>
          )}
          {collapsed[keyPath] && <span>...</span>}
          <div>{indentStr}<JSONBracket>]</JSONBracket></div>
        </>
      );
    }
    
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries?.length === 0) {
        return <JSONBracket>{'{}'}</JSONBracket>;
      }
      
      return (
        <>
          <ToggleButton onClick={() => toggleCollapse(keyPath)}>
            {collapsed[keyPath] ? '▸' : '▾'}
          </ToggleButton>
          <JSONBracket>{'{'}</JSONBracket>
          {!collapsed[keyPath] && (
            <CollapsibleObject>
              {entries.map(([k, v], index) => (
                <div key={k}>
                  {indentStr}  <JSONKey>"{k}"</JSONKey>: {renderValue(v, `${keyPath}-${k}`, indent + 1)}
                  {index < entries?.length - 1 && ','}
                </div>
              ))}
            </CollapsibleObject>
          )}
          {collapsed[keyPath] && <span>...</span>}
          <div>{indentStr}<JSONBracket>{'}'}</JSONBracket></div>
        </>
      );
    }
    
    return <span>{String(value)}</span>;
  };
  
  return <JSONContainer>{renderValue(data)}</JSONContainer>;
};

/**
 * RecordsModal Component
 * 
 * A modal component that displays multiple JSON records with expand/collapse functionality
 * 
 * @component
 * @example
 * ```tsx
 * <RecordsModal 
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   records={records}
 *   title="Filtered Records"
 * />
 * ```
 */
export const RecordsModal: React.FC<RecordsModalProps> = ({
  isOpen,
  onClose,
  records,
  title = 'Records'
}) => {
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={title}
      maxWidth="900px"
      maxHeight="85vh"
    >
      <RecordsContainer>
        <RecordsCount>Records: {records?.length}</RecordsCount>
        
        {records.map((record, index) => (
          <StyledAccordion
            key={index}
            title={`Record ${index + 1} Preview`}
            defaultCollapsed={true}
          >
            <RecordContent>
              <JSONRenderer data={record} depth={index} />
            </RecordContent>
          </StyledAccordion>
        ))}
      </RecordsContainer>
    </Modal>
  );
};

export default RecordsModal;

